import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import * as ReactTooltip from "react-tooltip";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import Tree, { ITree } from "src/components/common/Tree/Tree";
import { GroupItemTemplatesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { SettingsGroupsSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as GroupItemTemplateActions from "../../../actions/groupItemTemplate";
import * as GroupActions from "../../../actions/groups";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import { FormFilterInput } from "../../../components/form";
import { SelectFormInput } from "../../../components/form/index";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    findLookup,
    generateOptionsFromLookup,
} from "../../../helpers/lookupHelper";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import { GroupSettingsLayout } from "../../index";

import "./GroupItemTemplatesPage.scss";

interface GroupItemTemplatesPageProps {
    groups: Dtos.Group[];
    loadingGroups: boolean;
    loadGroupsSuccess: boolean;
    loadGroupsFailure: boolean;
    loadGroupsErrorCode: string;
    groupItemTemplates: Dtos.GroupItemTemplate[];
    groupItemsByCategory: Dtos.GroupItemTemplateCategory[];
    formFilter: IFormFilter;
    lookups: Dtos.Lookup[];
    loadingGroupItemTemplates: boolean;
    loadGroupItemTemplatesSuccess: boolean;
    loadGroupItemTemplatesFailure: boolean;
    loadGroupItemTemplatesErrorCode: string;
    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    clearGroups: GroupActions.IGroupClearActionCreator;
    loadGroups: GroupActions.IGroupLoadActionCreator;
    clearGroupItemTemplates: GroupItemTemplateActions.IGroupItemTemplateClearActionCreator;
    loadGroupItemTemplates: GroupItemTemplateActions.IGroupItemTemplateLoadActionCreator;
    filter: GroupItemTemplateActions.IGroupItemTemplateFilterActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const GroupItemTemplatesPage: FC<GroupItemTemplatesPageProps> = ({
    groups,
    loadingGroups,
    loadGroupsFailure,
    loadGroupsErrorCode,
    groupItemTemplates,
    groupItemsByCategory,
    formFilter,
    lookups,
    loadingGroupItemTemplates,
    loadGroupItemTemplatesSuccess,
    loadGroupItemTemplatesFailure,
    loadGroupItemTemplatesErrorCode,
    permissions,
    loadGroups,
    clearGroups,
    clearGroupItemTemplates,
    loadGroupItemTemplates,
    filter,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { groupId } = useParams<{
        groupId: string;
    }>();
    const groupIdNumber = parseInt(groupId, 10);

    //
    // -- States --
    const [groupIdState, setGroupIdState] = useState<number | null>(
        groupIdNumber
    );
    const [treeData, setTreeData] = useState<ITree | null>(null);
    const [initalizedTreeData, setInitalizedTreeData] =
        useState<boolean>(false);
    const [toggleState, setToggleState] = useState<string[]>([]);
    const [creatingTree, setCreatingTree] = useState<boolean>(false);

    //
    // -- Variables --
    const loading = loadingGroupItemTemplates || loadingGroups;
    const showGroupItems =
        !loadingGroupItemTemplates &&
        loadGroupItemTemplatesSuccess &&
        !creatingTree;

    //
    // -- Hooks --
    const navigate = useNavigate();


    // Load Groups, Trials & Items
    useEffect(() => {
        setDrawerItems(SettingsGroupsSubMenu(permissions));
        setBreadcrumbs(
            GroupItemTemplatesBreadcrumbs(
                isNaN(groupIdNumber) ? undefined : groupIdNumber
            )
        );

        // Dropdown options
        if (!groups || groups.length === 0) {
            loadGroups();
        }

        // Load templates
        if (
            groupIdState &&
            !loadingGroupItemTemplates &&
            !loadGroupItemTemplatesSuccess
        ) {
            loadGroupItemTemplates(groupIdState);
        }
    }, [
        loadGroups,
        setDrawerItems,
        SettingsGroupsSubMenu,
        setBreadcrumbs,
        GroupItemTemplatesBreadcrumbs,
        loadGroupItemTemplates,
        groups,
        groupIdState,
        groupItemTemplates,
        permissions,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadGroupsFailure) {
            navigate(`/error/${loadGroupsErrorCode}`);
        }
        if (loadGroupItemTemplatesFailure) {
            navigate(`/error/${loadGroupItemTemplatesErrorCode}`);
        }
    }, [
        loadGroupsFailure,
        loadGroupsErrorCode,
        loadGroupItemTemplatesFailure,
        loadGroupItemTemplatesErrorCode,
        navigate,
    ]);

    // Cleanup effect on component unmount
    useEffect(() => {
        return () => {
            clearGroups();
            clearGroupItemTemplates();
        };
    }, [clearGroups, clearGroupItemTemplates]);

    // Dropdown options memos
    const groupSelectOptions = useMemo(() => {
        if (!groups) return [];

        return groups
            ?.sort((a, b) => a?.name.localeCompare(b?.name))
            ?.map((x) => ({ value: x.id, label: x?.name }));
    }, [groups]);

    // Reset tree initalisation on form filter change
    useEffect(() => {
        setInitalizedTreeData(false);
    }, [groupItemsByCategory, formFilter, setInitalizedTreeData, groupIdState]);

    // Create tree
    useEffect(() => {
        if (groupItemsByCategory && !initalizedTreeData) {
            setCreatingTree(true);

            // If no group items return
            if (!groupItemsByCategory || groupItemsByCategory?.length == 0) {
                setTreeData(null);
                setCreatingTree(false);
                return;
            }

            const createChildNodes = (groupItems) => {
                let children = [];

                children =
                    groupItems
                        ?.sort((a, b) => a?.id - b?.id)
                        ?.map((y) => ({
                            id: `item-${y.id}`,
                            data: (
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 4fr 4fr 3fr",
                                    }}
                                >
                                    <div className="px-1">
                                        <FontAwesomeIcon
                                            icon={FontAwesomeIcons.Duotone.FILE}
                                        />
                                    </div>
                                    <div className="px-1">{y?.name}</div>
                                    <div className="d-inline-block px-1 text-truncate">
                                        <span
                                            className="text-truncate"
                                            data-tip={y?.description}
                                        >
                                            {y?.description}
                                            <ReactTooltip />
                                        </span>
                                    </div>
                                    <div className="px-1">
                                        {y?.initialVersion}
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: false,
                        })) ?? [];

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: "tableheader",
                        data: (
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 4fr 4fr 3fr",
                                }}
                            >
                                <div className="px-1"></div>
                                <div className="px-1">
                                    <strong>Name</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Description</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Initial Version</strong>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            let treeNodes =
                groupItemsByCategory
                    ?.sort((a, b) => a?.rank - b?.rank)
                    ?.map((x) => ({
                        id: `category-${x.id}`,
                        data: (
                            <div className="d-flex align-items-center">
                                <FontAwesomeIcon
                                    icon={FontAwesomeIcons.Duotone.ROLE}
                                />
                                <span
                                    className="pl-4 mb-0"
                                    style={{ width: "400px" }}
                                >
                                    {x?.value}
                                </span>
                                <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{ width: "50px" }}
                                >
                                    <span className="badge badge-pill badge-secondary ml-4 mb-0">
                                        {x?.groupItemTemplates?.length}
                                    </span>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: true,
                        subTree: {
                            children: [
                                ...createChildNodes(x.groupItemTemplates),
                            ],
                        },
                    })) ?? [];

            // Initialize tree toggle
            const expanded = treeNodes.map((z) => z.id);

            setToggleState(expanded);
            setTreeData({ children: treeNodes });
            setInitalizedTreeData(true);
            setCreatingTree(false);
        }
    }, [
        groupItemsByCategory,
        initalizedTreeData,
        treeData,
        formFilter,
        setTreeData,
        setToggleState,
        setInitalizedTreeData,
        setCreatingTree,
    ]);

    const handleToggle = useCallback(
        (id: string, navigateFlag?: boolean) => {
            if (navigateFlag) {
                if (
                    id.includes("item") &&
                    AuthenticationHelper.hasPermission(
                        permissions,
                        Dtos.Permission.GroupItemTemplatesView
                    )
                ) {
                    navigate(
                        groupIdState
                            ? `/settings/group/itemtemplates/${groupIdState}/template/${id.replace(
                                  "item-",
                                  ""
                              )}`
                            : `/settings/group/itemtemplates/template/${id.replace(
                                  "item-",
                                  ""
                              )}`
                    );
                }
                if (id.includes("category")) {
                    setToggleState((prev) =>
                        prev.includes(id)
                            ? prev.filter((nodeId) => nodeId !== id)
                            : [...prev, id]
                    );
                }
            } else {
                setToggleState((prev) =>
                    prev.includes(id)
                        ? prev.filter((nodeId) => nodeId !== id)
                        : [...prev, id]
                );
            }
        },
        [setToggleState, navigate, permissions]
    );

    const pageContent = useMemo(() => {
        return (
            <>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Group Item Templates</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <div
                            className="d-inline-table pr-2"
                            style={{ width: "300px" }}
                        >
                            <SelectFormInput
                                loading={loadingGroups}
                                multi={false}
                                options={groupSelectOptions}
                                clearable={true}
                                backspaceRemoves={false}
                                name={"groupIdState"}
                                id={"groupIdState"}
                                placeholder="Group"
                                onChange={function (
                                    value: any,
                                    _options?: any
                                ): any {
                                    clearGroupItemTemplates();
                                    setGroupIdState(
                                        value != undefined
                                            ? parseInt(value)
                                            : null
                                    );
                                    return undefined;
                                }}
                                value={groupIdState}
                            />
                        </div>
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.GroupItemTemplatesCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={
                                    groupIdState
                                        ? `/settings/group/itemtemplates/${groupIdState}/template/create`
                                        : "/settings/group/itemtemplates/template/create"
                                }
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {loading ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {!groupIdState ? (
                    <div className="card card-shadow p-3">
                        <div className="patient-tables">
                            <div className="alert alert-info d-flex mb-0">
                                <div className="mr-2">
                                    <FontAwesomeIcon
                                        icon={
                                            FontAwesomeIcons.Solid.INFO_SQUARE
                                        }
                                        fixedWidth
                                    />
                                </div>
                                <span>
                                    Select a group to view item templates.
                                </span>
                            </div>
                        </div>
                    </div>
                ) : null}
                {showGroupItems && groupIdState && !loading ? (
                    <>
                        {treeData && treeData?.children?.length > 0 ? (
                            <Tree
                                treeData={treeData}
                                onToggle={handleToggle}
                                toggleState={toggleState}
                            />
                        ) : (
                            <div className="card card-shadow p-3">
                                <div className="alert alert-info d-flex mb-0">
                                    <div className="mr-2">
                                        <FontAwesomeIcon
                                            icon={
                                                FontAwesomeIcons.Solid
                                                    .INFO_SQUARE
                                            }
                                            fixedWidth
                                        />
                                    </div>
                                    <span>
                                        There are currently no items to view.
                                    </span>
                                </div>
                            </div>
                        )}
                    </>
                ) : null}
            </>
        );
    }, [
        generateOptionsFromLookup,
        findLookup,
        lookups,
        showGroupItems,
        groupIdState,
        setGroupIdState,
        groupItemTemplates,
        groupSelectOptions,
        loadingGroups,
        clearGroupItemTemplates,
        treeData,
        handleToggle,
        toggleState,
        loading,
    ]);

    return (
        <GroupSettingsLayout
            permissions={permissions}
            permission={Dtos.Permission.GroupItemTemplatesView}
        >
            {pageContent}
        </GroupSettingsLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<GroupItemTemplatesPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        groups: state.groups.formFilteredData,
        loadingGroups:
            state.groups.loadState &&
            state.groups.loadState.status == RequestState.Pending,
        loadGroupsSuccess:
            state.groups.loadState &&
            state.groups.loadState.status == RequestState.Success,
        loadGroupsFailure:
            state.groups.loadState &&
            state.groups.loadState.status == RequestState.Failure,
        loadGroupsErrorCode: state.groups.loadState.errorCode,
        groupItemTemplates: state.groupItemTemplate.formFilteredData!,
        groupItemsByCategory: state.groupItemTemplate.formFilteredDataGrouped,
        formFilter: state.groupItemTemplate.formFilter,
        lookups: state.groupItemTemplate.lookups,
        loadingGroupItemTemplates:
            state.groupItemTemplate.loadState &&
            state.groupItemTemplate.loadState.status == RequestState.Pending,
        loadGroupItemTemplatesSuccess:
            state.groupItemTemplate.loadState &&
            state.groupItemTemplate.loadState.status == RequestState.Success,
        loadGroupItemTemplatesFailure:
            state.groupItemTemplate.loadState &&
            state.groupItemTemplate.loadState.status == RequestState.Failure,
        loadGroupItemTemplatesErrorCode:
            state.groupItemTemplate.loadState.errorCode,
        user: state.user.data,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
    };
};

const mapDispatchToProps = (dispatch): Partial<GroupItemTemplatesPageProps> => {
    return {
        loadGroups: bindActionCreators(GroupActions.LoadGroup, dispatch),
        clearGroups: bindActionCreators(GroupActions.Clear, dispatch),
        loadGroupItemTemplates: bindActionCreators(
            GroupItemTemplateActions.LoadGroupItemTemplate,
            dispatch
        ),
        clearGroupItemTemplates: bindActionCreators(
            GroupItemTemplateActions.Clear,
            dispatch
        ),
        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        ),
        filter: bindActionCreators(GroupItemTemplateActions.Filter, dispatch),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(GroupItemTemplatesPage);
